import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';

import {
  downloadDecisionAgentReview,
  submitDecisionAgentReview,
} from '../../services/api';
import DictationControl, { appendDictationTranscript } from './DictationControl';
import { agentOwner } from './agentDelivery';


export const REVIEW_REASON_OPTIONS = [
  ['scenario_complete', 'Scenario complete'],
  ['manual_review', 'Manual review'],
  ['repeated_clarification', 'Repeated clarification'],
  ['unresolved_request', 'Unresolved request'],
  ['tool_failure', 'Tool failure'],
  ['inconsistent_state', 'Inconsistent state'],
  ['severe_latency', 'Severe latency'],
  ['fallback_exhaustion', 'Fallback exhausted'],
  ['explicit_dissatisfaction', 'Explicit dissatisfaction'],
];

export const newReviewIdempotencyKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `review-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};


function ThreadReviewControl({ threadId, disabled = false }) {
  const owner = agentOwner();
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState('scenario_complete');
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isDictationBusy, setIsDictationBusy] = useState(false);
  const [dictationResetToken, setDictationResetToken] = useState(0);

  useEffect(() => {
    setOpen(false);
    setSubmission(null);
    setError(null);
    setPending(null);
    setNote('');
    setReasonCode('scenario_complete');
    setDictationResetToken((value) => value + 1);
  }, [threadId, owner]);

  const handleOpen = () => {
    setOpen(true);
    setSubmission(null);
    setError(null);
    try {
      const owner = agentOwner();
      const saved = JSON.parse(window.sessionStorage.getItem(`alloficient.agent.review.${owner}.${threadId}`) || 'null');
      if (saved && saved.owner === owner && saved.threadId === threadId) {
        setPending(saved);
        setReasonCode(saved.payload.reason_code);
        setNote(saved.payload.note);
      }
    } catch (_) {
      setError('The pending review could not be restored.');
    }
    setDictationResetToken((value) => value + 1);
  };

  const handleSubmit = async () => {
    const owner = agentOwner();
    if (!owner || (pending && pending.owner !== owner)) {
      setError('Sign in to the original profile before submitting this review.');
      return;
    }
    const request = pending || {
      owner, threadId, key: newReviewIdempotencyKey(), payload: { reason_code: reasonCode, note },
    };
    const storageKey = `alloficient.agent.review.${owner}.${threadId}`;
    setBusy(true);
    setError(null);
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(request));
      setPending(request);
      const response = await submitDecisionAgentReview(
        request.threadId, request.payload, request.key,
      );
      if (agentOwner() !== request.owner) return;
      window.sessionStorage.removeItem(storageKey);
      setPending(null);
      setSubmission(response.data);
    } catch (submitError) {
      if (agentOwner() !== request.owner) return;
      if ([400, 404].includes(submitError.response?.status)) {
        window.sessionStorage.removeItem(storageKey);
        setPending(null);
      }
      setError(
        submitError.response?.data?.error
        || 'The review could not be submitted. Please retry.',
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const response = await downloadDecisionAgentReview(submission.review_id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `accounting-agent-review-${submission.review_id}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(
        downloadError.response?.data?.error
        || 'The evidence bundle could not be downloaded. Please retry.',
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Tooltip title="Submit this conversation for human review">
        <span>
          <Button
            size="small"
            startIcon={<ReviewIcon />}
            onClick={handleOpen}
            disabled={!threadId || disabled}
            aria-label="Submit conversation for review"
          >
            Review
          </Button>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Submit conversation for review</DialogTitle>
        <DialogContent>
          {submission ? (
            <Box sx={{ pt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                The conversation frontier was sealed for review.
              </Alert>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                Review ID: {submission.review_id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Evidence SHA-256: {submission.snapshot_sha256}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                This seals the completed transcript, tool evidence, and proposal state at its
                current frontier. You can continue the conversation afterward.
              </Typography>
              <TextField
                select
                fullWidth
                label="Reason"
                disabled={busy || Boolean(pending)}
                value={reasonCode}
                onChange={(event) => setReasonCode(event.target.value)}
              >
                {REVIEW_REASON_OPTIONS.map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 0.5, pb: 2.5 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Reviewer note (optional)"
                  disabled={busy || Boolean(pending)}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  helperText={`${note.length}/2000`}
                />
                <DictationControl
                  active={open && !submission}
                  disabled={busy || Boolean(pending)}
                  idleLabel="Dictate reviewer note"
                  onBusyChange={setIsDictationBusy}
                  onTranscript={(transcript) => {
                    setNote((draft) => appendDictationTranscript(draft, transcript).slice(0, 2000));
                  }}
                  resetToken={dictationResetToken}
                />
              </Box>
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Close</Button>
          {submission ? (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Preparing…' : 'Download evidence'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={busy || isDictationBusy}
            >
              {busy ? 'Submitting…' : 'Submit for review'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ThreadReviewControl;
